import React, { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ReactPixel from 'react-facebook-pixel';
import classNames from 'classnames';
import MenuIcon from '@material-ui/icons/Menu';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  List,
  ListItem,
  ListItemText,
  Drawer,
  Button,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AppContext } from 'contexts';
import { KEYS } from 'config/constants';
import { images } from 'images';
import AgoraCall from 'views/Home/components/AgoraCall';
import { AgoraRTCClient } from 'utils/agora';

const useStyles = makeStyles({
  header: {
    height: 62,
    color: 'white',
  },
  topBox: {
    display: 'flex',
    backgroundColor: 'black',
    padding: '13px 15px',
  },
  infoBox: {
    flex: '1 1',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  infoTextDesktop: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',

    '& span': {
      flex: '1 1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    '& :not(img)': {
      '@media (max-width: 767px)': {
        display: 'none',
      },
    },
  },
  callingTextBox: {
    textAlign: 'center',
    backgroundColor: 'black',
    width: '100%',
  },
  desktopCallingTextBox: {
    alignSelf: 'center',
    marginRight: 5,
  },
  logo: {
    height: 32,
    margin: 'auto 20px',
  },
  topMenu: {
    flex: '1 1',
    display: 'flex',
    justifyContent: 'flex-end',
    fontSize: '20px',
    padding: 0,

    '@media (max-width: 767px)': {
      display: 'none',
    },
  },
  headerText: {
    fontSize: 10,
  },
  callIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px',
  },
  topMenuLink: {
    position: 'relative',
    width: 'auto',
    color: 'white',
    fontSize: '18px',
    textDecoration: 'none',
    padding: '0 5px',
    marginLeft: '10px',
    cursor: 'pointer',
    justifyContent: 'flex-end',
    '&:first-child': {
      marginLeft: 0,
    },

    '&:visited': {
      color: 'white',
    },

    '&.selected::after': {
      content: '""',
      position: 'absolute',
      height: '3px',
      left: 0,
      right: 0,
      bottom: -5,
      marginTop: '5px',
      backgroundColor: 'rgb(0, 188, 242)',
    },
  },
  menuButton: {
    flex: '1 1',
    display: 'none',

    '@media (max-width: 767px)': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },

    '& .MuiButton-root': {
      minWidth: 'auto',
    },
  },
  paper: {
    top: 102,
    bottom: 0,
    display: 'none',
    width: '60%',
    color: 'white',
    background: '#000A',

    '@media (max-width: 767px)': {
      display: 'flex',
    },
  },
  sideMenu: {
    padding: '30px',
  },
  sideMenuItem: {
    padding: '8px',
    cursor: 'pointer',

    '&.selected::after': {
      content: '""',
      position: 'absolute',
      height: '3px',
      left: 0,
      right: 0,
      bottom: 0,
      marginTop: '5px',
      backgroundColor: 'rgb(0, 188, 242)',
    },
  },
  infoTextMobile: {
    position: 'absolute',
    top: 61,
    left: 0,
    right: 0,
    height: 40,
    display: 'none',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    fontSize: 15,
    backgroundColor: '#0009',
    cursor: 'pointer',
    zIndex: 1000,
    paddingTop: 2,
    paddingBottom: 2,
    '@media (max-width: 767px)': {
      display: 'flex',
    },
  },
});

interface Props {
  className: string;
}

function Header({ className }: Props) {
  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  const appContext = useContext(AppContext);
  const {
    listing,
    listingId,
    videos,
    setInfoPropertyDisplay,
    setQRCodeDisplay,
    showLoginModal,
    isUserVerified,
    isCallOngoing,
    showConfirmModal,
    setModalMessage,
    displayBrokerReconnecting,
    isJoinedSession,
    stopRinging,
    setCallOngoing,
    sessionId,
  } = appContext;
  const [showMenu, setShowMenu] = useState(false);
  const [disableCallBroker, setDisableCallBroker] = useState(true);
  const hasQRCode = listing && listing.qr_code && listing.qr_code.qr_code;
  const videoCount = videos.length;
  const isVisitSelected = pathname.match(new RegExp(`/visit/${listingId}/*$`));
  const isVideoSelected = pathname.match(new RegExp(`/visit/${listingId}/video/*$`));
  const infoText = listing
    ? `${(listing.app ? `${listing.app}-` : '') + listing.address}, ${listing.city}`
    : null;

  useEffect(() => {
    const onUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = 'Write something clever here..';
      setCallOngoing(false);
      stopRinging(listing.broker.id, sessionId);
    };
    const vr_calling_enabled = listing?.broker?.vr_calling_enabled;
    const vr_calling_opt_in = listing?.broker?.vr_calling_opt_in;
    setDisableCallBroker(!vr_calling_enabled || !vr_calling_opt_in);
    if (isCallOngoing) {
      window.addEventListener('beforeunload', onUnload);
      return () => {
        window.removeEventListener('beforeunload', onUnload);
      };
    }
    return window.removeEventListener('beforeunload', onUnload);
  }, [listing, isCallOngoing]);
  const broker = listing ? listing.broker : null;
  const brokerFirstName = broker ? broker.firstname : '';
  const brokerLastName = broker ? broker.lastname : '';
  const getCallBrokerTitle = () => {
    if (isJoinedSession && !displayBrokerReconnecting) {
      return `En session avec le courtier ${brokerFirstName} ${brokerLastName}`;
    }
    if (displayBrokerReconnecting) {
      return `Le courtier ${brokerFirstName} ${brokerLastName} tente de se reconnecter...`;
    }
    return null;
  };
  const onInfoClick = () => {
    setInfoPropertyDisplay();
    ReactPixel.track('FindLocation', {
      content_name: infoText,
    });
  };

  const onVisitClick = () => {
    if (isVisitSelected) return;
    history.push(`/visit/${listingId}`);
  };

  const onVideoClick = () => {
    ReactPixel.track('ViewContent', {
      content_name: infoText,
      content_category: 'ViewVideo',
    });
    if (isVideoSelected) return;
    history.push(`/visit/${listingId}/video`);
  };

  const onQRCodeClick = () => {
    setQRCodeDisplay();
  };

  const handleCallButton = async () => {
    if (isUserVerified) {
      try {
        await AgoraRTCClient.init();
        showConfirmModal();
      } catch (error) {
        setModalMessage('init-error');
      }
    } else {
      showLoginModal();
    }
  };

  const onOpenMenu = () => {
    setShowMenu(true);
  };

  const onCloseMenu = () => {
    setShowMenu(false);
  };

  const logoClick = () => {
    window.open('https://www.hausvalet.ca');
  };

  const renderList = (listClass: string, linkClass: string) => {
    return (
      <List className={listClass}>
        <div className={classes.desktopCallingTextBox}>
          <Typography className={classes.headerText}>{getCallBrokerTitle()}</Typography>
        </div>
        {!disableCallBroker &&
          (isCallOngoing ? (
            <AgoraCall />
          ) : (
            <ListItem
              className={classNames(linkClass)}
              onClick={handleCallButton}
              onTouchEnd={handleCallButton}
            >
              <img
                src='/images/crop_call_blue_transparent.gif'
                alt='call'
                height='30px'
                width='30px'
              />
              <Typography style={{ fontSize: 15, marginLeft: 5 }}>
                Visite en direct
              </Typography>
            </ListItem>
          ))}
        {videoCount ? (
          <ListItem
            className={classNames(linkClass, { selected: !!isVisitSelected })}
            onClick={onVisitClick}
            onTouchEnd={onVisitClick}
          >
            <ListItemText>Visite 3D</ListItemText>
          </ListItem>
        ) : null}

        {videoCount ? (
          <ListItem
            className={classNames(linkClass, { selected: !!isVideoSelected })}
            onClick={onVideoClick}
            onTouchEnd={onVideoClick}
          >
            <ListItemText>{`Vidéo [${videoCount}]`}</ListItemText>
          </ListItem>
        ) : null}
      </List>
    );
  };

  return (
    <div className={classNames(className, classes.header)}>
      <div className={classes.topBox}>
        <div className={classes.infoBox}>
          {listing && (
            <div
              className={classes.infoTextDesktop}
              onClick={onInfoClick}
              onTouchEnd={onInfoClick}
            >
              <img
                src='/images/circle_i.png'
                alt='Loading...'
                style={{ marginRight: 10 }}
              />
              <span>{infoText}</span>
              <ExpandMoreIcon style={{ color: 'white' }} />
            </div>
          )}
        </div>

        <div style={{ margin: 'auto' }} onClick={logoClick} onTouchEnd={logoClick}>
          <img className={classes.logo} src={images.logoTop} alt='Logo' />
        </div>
        {/* desktop menu */}
        {renderList(classes.topMenu, classes.topMenuLink)}
        {/* mobile menu */}
        <div className={classes.menuButton}>
          {videoCount || !disableCallBroker ? (
            <>
              <Button onClick={onOpenMenu} onTouchEnd={onOpenMenu}>
                <MenuIcon style={{ color: 'white' }} />
              </Button>
              <Drawer
                classes={{ paper: classes.paper }}
                anchor='right'
                open={showMenu}
                onClose={onCloseMenu}
              >
                {renderList(classes.sideMenu, classes.sideMenuItem)}
              </Drawer>
            </>
          ) : null}
        </div>
        {hasQRCode ? (
          <div
            className={classes.infoTextDesktop}
            onClick={onQRCodeClick}
            onTouchEnd={onQRCodeClick}
          >
            <img
              src='/images/vr_headset_small.png'
              alt='Loading...'
              style={{ marginLeft: 10 }}
            />
            <ExpandMoreIcon style={{ color: 'white' }} />
          </div>
        ) : null}
      </div>
      <div
        className={classes.infoTextMobile}
        onClick={onInfoClick}
        onTouchEnd={onInfoClick}
      >
        <div className={classes.callingTextBox}>
          <Typography>{getCallBrokerTitle()}</Typography>
        </div>
        <div>{infoText}</div>
      </div>
    </div>
  );
}

Header.defaultProps = {
  className: null,
};

Header.propTypes = {
  className: PropTypes.string,
};

export default Header;
